import { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ArrowBack from '@mui/icons-material/ArrowBack'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { authService } from '../services/authService'
import LoadingAnimation from '../components/common/LoadingAnimation'
import RoleSelect from '../components/common/RoleSelect'
import { getPasswordPolicyState, isPasswordPolicySatisfied } from '../utils/passwordPolicy'

const FAVICON = '/favicon.png'
const LOGO_FALLBACK = '/assets/images/ui_elements/logo.png'

const USERNAME_DEBOUNCE_MS = 420

const RegisterPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    role: 'parent',
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [usernameAvailable, setUsernameAvailable] = useState(null)
  const [usernameChecking, setUsernameChecking] = useState(false)
  const usernameCheckVersion = useRef(0)

  const usernameTrim = formData.username.trim()
  const passwordPolicy = useMemo(
    () => getPasswordPolicyState(formData.password),
    [formData.password]
  )
  const passwordValid = isPasswordPolicySatisfied(formData.password)
  const confirmTrim = formData.confirmPassword
  const passwordsMatch =
    formData.password.length > 0 &&
    confirmTrim.length > 0 &&
    formData.password === confirmTrim
  const showConfirmMismatch = confirmTrim.length > 0 && !passwordsMatch

  useEffect(() => {
    if (usernameTrim.length < 3) {
      usernameCheckVersion.current += 1
      setUsernameAvailable(null)
      setUsernameChecking(false)
      return
    }

    const version = ++usernameCheckVersion.current
    setUsernameChecking(true)
    const timer = setTimeout(async () => {
      try {
        const data = await authService.checkUsername(usernameTrim)
        if (version !== usernameCheckVersion.current) return
        setUsernameAvailable(data.available)
      } catch (e) {
        if (version !== usernameCheckVersion.current) return
        if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return
        setUsernameAvailable(null)
      } finally {
        if (version === usernameCheckVersion.current) setUsernameChecking(false)
      }
    }, USERNAME_DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
    }
  }, [usernameTrim])

  const usernameOk =
    usernameTrim.length >= 3 && usernameAvailable === true && !usernameChecking
  const canSubmit =
    usernameOk &&
    passwordValid &&
    passwordsMatch &&
    formData.email.trim().length > 0 &&
    !loading

  const policyRows = useMemo(
    () => [
      { key: 'minLength', label: t('auth.passwordPolicyMinLength') },
      { key: 'hasLower', label: t('auth.passwordPolicyLower') },
      { key: 'hasUpper', label: t('auth.passwordPolicyUpper') },
      { key: 'hasDigit', label: t('auth.passwordPolicyDigit') },
      { key: 'hasSpecial', label: t('auth.passwordPolicySpecial') },
      { key: 'noSequentialDigits', label: t('auth.passwordPolicyNoSequentialDigits') },
      { key: 'notWeak', label: t('auth.passwordPolicyNotWeak') },
    ],
    [t]
  )

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!canSubmit) return

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'))
      return
    }

    setLoading(true)

    try {
      const userData = {
        email: formData.email,
        username: usernameTrim,
        password: formData.password,
        role: formData.role,
      }
      await authService.register(userData)
      navigate('/login', {
        replace: true,
        state: { successMessage: t('auth.registrationSuccess') },
      })
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.registrationFailed'))
    } finally {
      setLoading(false)
    }
  }

  const inputBase =
    'appearance-none relative block w-full ps-3 md:ps-4 py-2 md:py-2.5 border placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm md:text-base'
  /** Physical left/right padding so RTL placeholder text does not sit under the visibility toggle (eye stays left). */
  const passwordInputCore =
    'appearance-none relative block w-full py-2 md:py-2.5 border placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 text-sm md:text-base pl-12 md:pl-14'

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white py-8 md:py-12 px-4 sm:px-6 lg:px-8">
      <Link
        to="/"
        className="absolute start-4 top-6 flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 sm:start-6 md:start-8"
        aria-label={t('common.back')}
      >
        <ArrowBack sx={{ fontSize: 20 }} className="rtl:scale-x-[-1]" />
        <span className="hidden sm:inline">{t('common.back')}</span>
      </Link>
      <div className="max-w-md w-full space-y-6 md:space-y-8 bg-white rounded-lg shadow-lg p-6 md:p-8">
        <div className="text-center">
          <Link
            to="/"
            className="inline-block focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-full"
          >
            <img
              src={FAVICON}
              alt={t('common.appName')}
              className="w-20 h-20 md:w-28 md:h-28 mx-auto mb-2 object-contain rounded-full"
              onError={(e) => {
                e.target.src = LOGO_FALLBACK
                e.target.onerror = null
                e.target.classList.remove('rounded-full')
              }}
            />
          </Link>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
            {t('common.register')}
          </h2>
        </div>
        <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 md:px-4 py-2 md:py-3 rounded flex items-center gap-2">
              <img
                src="/assets/images/ui_elements/icon_warning.png"
                alt="Error"
                className="w-5 h-5 flex-shrink-0"
                onError={(e) => {
                  e.target.src = '/assets/images/ui_elements/icon_error.png'
                }}
              />
              <span className="text-sm md:text-base">{error}</span>
            </div>
          )}
          <div className="space-y-3 md:space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('auth.email')}</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`${inputBase} border-gray-300 focus:border-primary-500 pr-3 md:pr-4`}
                placeholder={t('auth.placeholderEmail')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('auth.username')}</label>
              <div className="relative">
                <input
                  type="text"
                  name="username"
                  required
                  minLength={3}
                  maxLength={50}
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full py-2 md:py-2.5 border placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm md:text-base pr-3 md:pr-4 ${
                    usernameOk ? 'pl-11 md:pl-12' : 'pl-3 md:pl-4'
                  } ${
                    usernameTrim.length >= 3 && usernameAvailable === false
                      ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
                      : usernameOk
                        ? 'border-green-400 focus:border-primary-500'
                        : 'border-gray-300 focus:border-primary-500'
                  }`}
                  placeholder={t('auth.placeholderUsername')}
                />
                {usernameOk && (
                  <CheckCircle
                    className="pointer-events-none absolute left-2 top-1/2 z-10 -translate-y-1/2 text-green-600"
                    sx={{ fontSize: 22 }}
                    aria-hidden
                  />
                )}
              </div>
              {usernameTrim.length >= 3 && usernameChecking && (
                <p className="mt-1.5 text-xs text-gray-500">{t('auth.usernameChecking')}</p>
              )}
              {usernameTrim.length >= 3 && !usernameChecking && usernameAvailable === false && (
                <p className="mt-1.5 text-xs font-medium text-red-600">{t('auth.usernameTaken')}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('auth.role')}</label>
              <RoleSelect name="role" value={formData.role} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('auth.password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`${passwordInputCore} border-gray-300 focus:border-primary-500 focus:ring-primary-500 ${
                    passwordValid && formData.password ? 'pr-11 md:pr-12' : 'pr-3 md:pr-4'
                  }`}
                  placeholder={t('auth.placeholderPassword')}
                />
                {passwordValid && formData.password.length > 0 && (
                  <CheckCircle
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-green-600"
                    sx={{ fontSize: 22 }}
                    aria-hidden
                  />
                )}
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPassword ? <VisibilityOff sx={{ fontSize: 22 }} /> : <Visibility sx={{ fontSize: 22 }} />}
                </button>
              </div>
              {formData.password.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs md:text-sm">
                  {policyRows.map(({ key, label }) => (
                    <li
                      key={key}
                      className={`flex items-center gap-2 transition-colors ${
                        passwordPolicy[key] ? 'font-medium text-green-600' : 'text-gray-500'
                      }`}
                    >
                      <span
                        className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[10px] ${
                          passwordPolicy[key]
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-300 bg-white text-gray-400'
                        }`}
                      >
                        {passwordPolicy[key] ? '✓' : ''}
                      </span>
                      {label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('auth.confirmPassword')}</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  name="confirmPassword"
                  required
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`${passwordInputCore} ${
                    showConfirmMismatch
                      ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
                      : passwordsMatch
                        ? 'border-green-400 focus:border-primary-500 focus:ring-primary-500'
                        : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                  } ${passwordsMatch && confirmTrim ? 'pr-11 md:pr-12' : 'pr-3 md:pr-4'}`}
                  placeholder={t('auth.placeholderConfirmPassword')}
                />
                {passwordsMatch && (
                  <CheckCircle
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-green-600"
                    sx={{ fontSize: 22 }}
                    aria-hidden
                  />
                )}
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showConfirm ? <VisibilityOff sx={{ fontSize: 22 }} /> : <Visibility sx={{ fontSize: 22 }} />}
                </button>
              </div>
              {showConfirmMismatch && (
                <p className="mt-1.5 text-xs font-medium text-red-600">{t('auth.confirmPasswordMismatch')}</p>
              )}
            </div>
          </div>
          <div>
            <button
              type="submit"
              disabled={!canSubmit}
              className="group relative w-full flex justify-center items-center py-2.5 md:py-3 border border-transparent text-sm md:text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? <LoadingAnimation size="sm" /> : t('common.register')}
            </button>
          </div>
          <div className="text-center">
            <Link
              to="/login"
              className="text-sm md:text-base text-primary-600 hover:text-primary-500 font-medium"
            >
              {t('auth.alreadyHaveAccount')} {t('common.login')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}

export default RegisterPage
